import React from 'react';
import { Link } from 'react-router-dom';
import { FiFileText, FiUser, FiShield, FiAlertCircle, FiCheck, FiMail, FiServer, FiX } from 'react-icons/fi';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';

const TermsOfService = () => {
  const sections = [
    {
      id: 'agreement',
      title: 'Agreement to Terms',
      icon: FiFileText,
      content: `By accessing and using Tasklina, you accept and agree to be bound by these Terms of Service. These terms govern your use of our AI-powered task management platform and all related services. If you do not agree with any part of these terms, you may not access the service.`
    },
    {
      id: 'service-description',
      title: 'Description of Service',
      icon: FiServer,
      content: `Tasklina is a comprehensive task management and project collaboration platform that enables:`,
      items: [
        'Creating and managing tasks, projects, and workflows',
        'Real-time team collaboration and communication',
        'AI-powered insights and task recommendations',
        'File storage and document sharing',
        'Calendar integration and scheduling',
        'Organization and team management'
      ]
    },
    {
      id: 'user-accounts',
      title: 'User Accounts',
      icon: FiUser,
      subsections: [
        {
          title: 'Account Registration',
          items: [
            'You must provide accurate and complete information',
            'You are responsible for maintaining account security',
            'You must be at least 13 years old to use our service',
            'One person or legal entity may maintain only one free account'
          ]
        },
        {
          title: 'Account Responsibilities',
          items: [
            'Keep your login credentials secure and confidential',
            'Notify us immediately of any unauthorized access',
            'You are responsible for all activities under your account',
            'Do not share your account with others'
          ]
        }
      ]
    },
    {
      id: 'acceptable-use',
      title: 'Acceptable Use',
      icon: FiShield,
      content: `You agree to use Tasklina responsibly. The following activities are strictly prohibited:`,
      prohibitedItems: [
        'Violating any applicable laws or regulations',
        'Infringing on intellectual property rights',
        'Uploading malicious software or harmful content',
        'Attempting unauthorized access to our systems',
        'Using the service for spam or harassment',
        'Sharing inappropriate or offensive content',
        'Interfering with other users\' experience'
      ]
    },
    {
      id: 'content-ownership',
      title: 'Content & Data Ownership',
      icon: FiFileText,
      content: `You retain ownership of all content you create, upload, or share through Tasklina. By using our service, you grant us a limited license to use, store, and display your content solely for providing our services.`,
      items: [
        'Your data remains yours - we never sell it',
        'You can export your data at any time',
        'We may remove content that violates our terms',
        'Backup your important data regularly'
      ]
    },
    {
      id: 'termination',
      title: 'Account Termination',
      icon: FiAlertCircle,
      items: [
        'You may terminate your account at any time from settings',
        'We may suspend accounts that violate these terms',
        'Upon termination, your access ends immediately',
        'Data is retained for 30 days before permanent deletion',
        'You can request immediate data deletion if needed'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicPageHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-3 bg-blue-100 rounded-2xl">
              <FiFileText className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Terms of Service</h1>
              <p className="text-gray-500 mt-1">Last updated: December 17, 2025</p>
            </div>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
            Please read these terms carefully before using Tasklina. By using our service, you agree to be bound by these terms.
          </p>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap gap-3">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-full hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <main className="py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="space-y-12">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <section 
                  key={section.id} 
                  id={section.id}
                  className="scroll-mt-24"
                >
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="p-2.5 bg-blue-50 rounded-xl">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <h2 className="text-2xl font-semibold text-gray-900 pt-1">
                        {section.title}
                      </h2>
                    </div>

                    {section.content && (
                      <p className="text-gray-600 leading-relaxed mb-6 pl-14">
                        {section.content}
                      </p>
                    )}

                    {section.subsections && (
                      <div className="space-y-6 pl-14">
                        {section.subsections.map((sub, idx) => (
                          <div key={idx}>
                            <h3 className="text-lg font-medium text-gray-800 mb-3">
                              {sub.title}
                            </h3>
                            <ul className="space-y-2.5">
                              {sub.items.map((item, itemIdx) => (
                                <li key={itemIdx} className="flex items-start space-x-3">
                                  <FiCheck className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-600">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {section.items && (
                      <ul className="space-y-3 pl-14">
                        {section.items.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <FiCheck className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.prohibitedItems && (
                      <ul className="space-y-3 pl-14">
                        {section.prohibitedItems.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <FiX className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              );
            })}

            {/* Contact Section */}
            <section className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <div className="flex items-start space-x-4">
                <div className="p-2.5 bg-white/20 rounded-xl">
                  <FiMail className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold mb-3">Questions About Our Terms?</h2>
                  <p className="text-blue-100 mb-6 leading-relaxed">
                    If you have any questions about these Terms of Service, please contact us. 
                    We're here to help clarify anything you need.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <a 
                      href="mailto:legal@tasklina.com"
                      className="inline-flex items-center px-5 py-2.5 bg-white text-blue-600 rounded-full font-medium hover:bg-blue-50 transition-colors"
                    >
                      <FiMail className="w-4 h-4 mr-2" />
                      legal@tasklina.com
                    </a>
                    <Link 
                      to="/contact"
                      className="inline-flex items-center px-5 py-2.5 bg-blue-500 text-white rounded-full font-medium hover:bg-blue-400 transition-colors"
                    >
                      Contact Us
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <PublicPageFooter />
    </div>
  );
};

export default TermsOfService;
